import { EmployeeAssignmentTypes } from 'models/enums';
import { ReportType } from 'models/enums/typings';
import { getImprovementsSpecialist } from 'pages/PostInspectionFlow/steps/Improvements/utils';
import { EMPLOYEE_TITLES } from 'strings/common.strings';

type Params = {
  listings: {
    homeListingDetailsModel: {
      basicInfo: {
        unitId: string;
      };
      employeeAssignments: {
        employeeAssignment: {
          assignmentType: string;
        };
        employee: {
          bannerImageUrl: string;
        };
        user: {
          profileImageUrl: string;
        };
      }[];
    };
  }[];
  reportType: keyof typeof ReportType;
  flow: {
    configuration: {
      flowSubType: string;
      homeUniqueId: string;
    };
  };
};

type Result = {
  employee:
    | {
        bannerImage: string;
        jobTitle: string;
        profileImage: string;
      }
    | undefined;
};

// The return type includes `undefined`: the Pricing branch is still a TODO
// below and falls through to a bare `return undefined`.
export const useEmployee = ({
  listings,
  flow,
  reportType,
}: Params): Result | undefined => {
  const { flowSubType, homeUniqueId } = flow.configuration;

  const currentListing = listings.find(
    (listing) =>
      listing.homeListingDetailsModel.basicInfo.unitId === homeUniqueId,
  );

  if (reportType === ReportType.ImprovementsSpecialist) {
    const employee = getImprovementsSpecialist(
      currentListing?.homeListingDetailsModel,
    );

    return { employee };
  }

  if (reportType === ReportType.Improvements) {
    // Asserted: `currentListing` comes from a `.find`, and this destructure
    // already threw when the home was absent from `listings`.
    const {
      homeListingDetailsModel: { employeeAssignments },
    } = currentListing!;

    const employeeCharge =
      flowSubType === 'Onboarding' // todo: check if these contant can be fetched from Swagger types
        ? EmployeeAssignmentTypes.HomeownerGrowth
        : EmployeeAssignmentTypes.Concierge;

    const improvementsReportMainPointOfContact = employeeAssignments.find(
      (employeeAssignment) =>
        employeeAssignment.employeeAssignment.assignmentType === employeeCharge,
    );

    // Asserted for the same reason as above, and the assignment type is cast
    // because `models/enums` is plain JS, so its members widen to `string` and
    // cannot index `EMPLOYEE_TITLES`.
    const mainPointOfContact = improvementsReportMainPointOfContact!;

    const employee = {
      ...mainPointOfContact.employee,
      ...mainPointOfContact.user,
      bannerImage: mainPointOfContact.employee.bannerImageUrl,
      jobTitle:
        EMPLOYEE_TITLES[
          mainPointOfContact.employeeAssignment
            .assignmentType as keyof typeof EMPLOYEE_TITLES
        ],
      profileImage: mainPointOfContact.user.profileImageUrl,
    };

    return { employee };
  }

  // todo: complete this case once we migrate Pricing Plan to post-inspection folder
  return undefined;
};
